import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient from '../services/api'
import { useAuth } from '../contexts/AuthContext'

interface Campaign {
  id: number
  subject: string
  recipient_company: string
  recipient_name: string
  open_count: number
  opened_at?: string
  reply_status: string
  reply_summary?: string
  status: string
  sent_at?: string
}

const POLL_INTERVAL = 60000 // 1 minute
const OPEN_STORAGE_KEY = 'spread_last_open_counts'
const REPLY_STORAGE_KEY = 'spread_last_reply_statuses'

const HotProspectNotification: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [hotProspects, setHotProspects] = useState<Campaign[]>([])
  const [newReplies, setNewReplies] = useState<Campaign[]>([])
  const [dismissed, setDismissed] = useState<number[]>([])
  const [dismissedReplies, setDismissedReplies] = useState<number[]>([])
  const prevCountsRef = useRef<Record<number, number>>({})
  const prevReplyRef = useRef<Record<number, string>>({})

  useEffect(() => {
    // This sits above the routes, so it is mounted on the sign-in page and the
    // landing page too — where there is nobody to notify and no token to ask
    // with. It used to poll there anyway: three 401s on every load, and two
    // more every minute for as long as the tab stayed on the sign-in screen.
    if (!isAuthenticated) return

    try {
      prevCountsRef.current = JSON.parse(localStorage.getItem(OPEN_STORAGE_KEY) || '{}')
      prevReplyRef.current = JSON.parse(localStorage.getItem(REPLY_STORAGE_KEY) || '{}')
    } catch { /* ignore */ }

    // check-replies-batch once on mount to get fresh reply data
    apiClient.post('/email-campaigns/check-replies-batch?hours_since_sent=72&limit=20').catch(() => {})

    const check = async () => {
      try {
        const res = await apiClient.get('/email-campaigns?limit=100')
        const campaigns: Campaign[] = res.data

        const newHot: Campaign[] = []
        const newCounts: Record<number, number> = {}
        const freshReplies: Campaign[] = []
        const newReplyStatuses: Record<number, string> = {}

        for (const c of campaigns) {
          const currentCount = c.open_count || 0
          const prevCount = prevCountsRef.current[c.id] || 0
          newCounts[c.id] = currentCount
          if (currentCount > prevCount && c.status === 'sent' && c.reply_status === 'no_reply') {
            newHot.push(c)
          }

          const currentReply = c.reply_status || 'no_reply'
          const prevReply = prevReplyRef.current[c.id] || 'no_reply'
          newReplyStatuses[c.id] = currentReply
          if (prevReply === 'no_reply' && currentReply !== 'no_reply' && c.status === 'sent') {
            freshReplies.push(c)
          }
        }

        prevCountsRef.current = newCounts
        prevReplyRef.current = newReplyStatuses
        localStorage.setItem(OPEN_STORAGE_KEY, JSON.stringify(newCounts))
        localStorage.setItem(REPLY_STORAGE_KEY, JSON.stringify(newReplyStatuses))

        if (newHot.length > 0) {
          setHotProspects(prev => {
            const existingIds = new Set(prev.map(p => p.id))
            return [...prev, ...newHot.filter(p => !existingIds.has(p.id))]
          })
        }
        if (freshReplies.length > 0) {
          setNewReplies(prev => {
            const existingIds = new Set(prev.map(p => p.id))
            return [...prev, ...freshReplies.filter(p => !existingIds.has(p.id))]
          })
        }
      } catch { /* ignore */ }
    }

    check()
    const interval = setInterval(check, POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [isAuthenticated])

  const visibleOpens = hotProspects.filter(p => !dismissed.includes(p.id))
  const visibleReplies = newReplies.filter(p => !dismissedReplies.includes(p.id))
  if (visibleOpens.length === 0 && visibleReplies.length === 0) return null

  const dismiss = (id: number) => setDismissed(prev => [...prev, id])
  const dismissAllOpens = () => setDismissed(hotProspects.map(p => p.id))
  const dismissReply = (id: number) => setDismissedReplies(prev => [...prev, id])

  const minutesAgo = (d?: string) => {
    if (!d) return null
    const mins = Math.round((Date.now() - new Date(d).getTime()) / 60000)
    if (mins < 1) return '今'
    if (mins < 60) return `${mins}分前`
    return `${Math.round(mins / 60)}時間前`
  }

  const replyLabel = (status: string) => {
    if (status === 'positive') return '興味あり'
    if (status === 'negative') return '不要'
    if (status === 'meeting_requested') return '打ち合わせ希望'
    return '返信あり'
  }

  return (
    <div
      role="region"
      aria-label="リード通知"
      style={{
        position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 1000,
        display: 'flex', flexDirection: 'column', gap: '0.5rem',
        maxWidth: '360px', width: 'calc(100vw - 3rem)'
      }}
    >
      {/* Reply notifications (shown first, blue) */}
      {visibleReplies.slice(0, 2).map(p => (
        <div
          key={`reply-${p.id}`}
          role="alert"
          aria-live="assertive"
          style={{
            backgroundColor: 'white', border: '2px solid #2563eb',
            borderRadius: '0.75rem', padding: '0.875rem 1rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            animation: 'slideIn 0.3s ease'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.125rem' }}>💬</span>
              <span style={{ fontWeight: '700', fontSize: '0.875rem', color: '#1d4ed8' }}>
                返信が届きました！
              </span>
            </div>
            <button
              onClick={() => dismissReply(p.id)}
              aria-label="通知を閉じる"
              // A 44px hit area around a small ×: the glyph does not have to
              // grow for the tap target to.
              style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
                fontSize: '1rem', lineHeight: 1,
                width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '-0.625rem -0.625rem -0.625rem 0',
              }}
            >
              ×
            </button>
          </div>

          <p style={{ margin: '0 0 0.25rem', fontWeight: '600', fontSize: '0.9375rem', color: '#111827' }}>
            {p.recipient_company}
          </p>
          <p style={{ margin: '0 0 0.625rem', fontSize: '0.8125rem', color: '#6b7280' }}>
            {p.recipient_name} — <span style={{ color: '#2563eb', fontWeight: '600' }}>{replyLabel(p.reply_status)}</span>
            {p.reply_summary && (
              <span style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                {p.reply_summary.slice(0, 60)}{p.reply_summary.length > 60 ? '…' : ''}
              </span>
            )}
          </p>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => { navigate('/reply-inbox'); dismissReply(p.id) }}
              style={{
                flex: 1, minHeight: 44, boxSizing: 'border-box', padding: '0.5rem',
                fontSize: '0.8125rem', fontWeight: '700',
                backgroundColor: '#2563eb', color: 'white', border: 'none',
                borderRadius: '0.375rem', cursor: 'pointer'
              }}
            >
              返信を確認する
            </button>
            <button
              onClick={() => dismissReply(p.id)}
              style={{
                minHeight: 44, boxSizing: 'border-box', padding: '0.5rem 0.625rem',
                fontSize: '0.8125rem',
                backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none',
                borderRadius: '0.375rem', cursor: 'pointer'
              }}
            >
              後で
            </button>
          </div>
        </div>
      ))}

      {/* Open notifications (orange) */}
      {visibleOpens.slice(0, 3).map(p => (
        <div
          key={p.id}
          role="status"
          aria-live="polite"
          style={{
            backgroundColor: 'white', border: '2px solid #d97706',
            borderRadius: '0.75rem', padding: '0.875rem 1rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            animation: 'slideIn 0.3s ease'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.125rem' }}>🔥</span>
              <span style={{ fontWeight: '700', fontSize: '0.875rem', color: '#92400e' }}>
                ホットリード！
              </span>
            </div>
            <button
              onClick={() => dismiss(p.id)}
              aria-label="通知を閉じる"
              style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af',
                fontSize: '1rem', lineHeight: 1,
                width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '-0.625rem -0.625rem -0.625rem 0',
              }}
            >
              ×
            </button>
          </div>

          <p style={{ margin: '0 0 0.25rem', fontWeight: '600', fontSize: '0.9375rem', color: '#111827' }}>
            {p.recipient_company}
          </p>
          <p style={{ margin: '0 0 0.625rem', fontSize: '0.8125rem', color: '#6b7280' }}>
            {p.recipient_name}がメールを開封しました
            {p.open_count > 1 && ` (${p.open_count}回目)`}
            {p.opened_at && <span style={{ marginLeft: '0.375rem', color: '#d97706' }}>— {minutesAgo(p.opened_at)}</span>}
          </p>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => { navigate('/reply-inbox'); dismiss(p.id) }}
              style={{
                flex: 1, minHeight: 44, boxSizing: 'border-box', padding: '0.5rem',
                fontSize: '0.8125rem', fontWeight: '700',
                backgroundColor: '#d97706', color: 'white', border: 'none',
                borderRadius: '0.375rem', cursor: 'pointer'
              }}
            >
              今すぐフォローアップ
            </button>
            <button
              onClick={() => dismiss(p.id)}
              style={{
                minHeight: 44, boxSizing: 'border-box', padding: '0.5rem 0.625rem',
                fontSize: '0.8125rem',
                backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none',
                borderRadius: '0.375rem', cursor: 'pointer'
              }}
            >
              後で
            </button>
          </div>
        </div>
      ))}

      {visibleOpens.length > 3 && (
        <div style={{
          backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '0.5rem',
          minHeight: 44, boxSizing: 'border-box',
          padding: '0.5rem 0.875rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span style={{ fontSize: '0.8125rem', color: '#92400e', fontWeight: '500' }}>
            他 {visibleOpens.length - 3}件のホットリード
          </span>
          <button
            onClick={() => { navigate('/drafts?role=send&bucket=sent'); dismissAllOpens() }}
            style={{
              minHeight: 44, boxSizing: 'border-box', padding: '0.5rem 0',
              fontSize: '0.8125rem', color: '#d97706', fontWeight: '600',
              background: 'none', border: 'none', cursor: 'pointer',
            }}
          >
            全て確認 →
          </button>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  )
}

export default HotProspectNotification
